import { useId, useState } from "react";
import { useNavigate } from "react-router";

import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import {
  libraryErrorField,
  useAssetVersionQuery,
  useCreateLibraryAssetMutation,
  useLibraryAssetsQuery,
  useLibraryPartManifestQuery,
  type LibraryAsset,
} from "@/api/library";
import {
  useSummaryTargetCatalogQuery,
  type SummaryTarget,
  type SummaryTargetCatalog,
} from "@/api/summaryTargets";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import {
  isPartsContent,
  toDraft,
  toPartsContent,
  type DraftPart,
} from "@/lib/assetContent";
import { UNWRITTEN_PROMPT_TEXT, manifestFor, requiredRoles } from "@/lib/namedParts";
import {
  diagnosticTargetKey,
  evaluationsOfGroup,
  occupiedSummaryTargets,
  sectionTargetKey,
  summaryTargetKey,
  type SummaryTargetHolder,
} from "@/lib/summaryTargets";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { FieldError, joinDescriptionIds } from "@/ui/FieldError";
import { Field, Input, Select } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { LoadingState, UnavailableState } from "@/ui/states";

import {
  NamedReadFailureState,
  type NamedReadFailure,
} from "./NamedReadFailure";

/**
 * Create a Summary prompt — one purpose, and the target SELECTED (#863).
 *
 * ## THE FIELD THIS DIALOG DOES NOT HAVE
 *
 * There is no "Pack placement" text box, and that absence is the ticket. The
 * generic `NewAssetDialog` and `NewMainConcernDialog` both ask a clinician to
 * type a placement coordinate; for a Summary prompt the committed MOD/NII
 * equivalence contract forbids exactly that — "no free-form nested path" — and
 * #862 makes `POST /library/assets` refuse a `placementCoordinate` carrying a
 * `/` for this family outright. The Lovable reference's own New-section dialog
 * is a free-text `Name` field with "Lowercase with underscores" underneath,
 * which is the same input with friendlier copy: it can produce
 * `clinicial_summary`, a target the summary agent never opens, and it cannot
 * produce the depression target at all.
 *
 * So the placement is CHOSEN from `GET /library/summary-targets`. A section is
 * one select; a diagnostic finding is a group and an evaluation, dependent, both
 * from the same read. Nothing here builds a path — the directory shown under the
 * choice is the server's own expansion of its own template — and nothing here
 * knows which sections exist.
 *
 * ## THE LANGUAGE IS A REAL FIELD, NOT A DEFAULT THIS FILE PICKS
 *
 * A Summary prompt's part key is language-prefixed, and
 * `adoption_serializer._serialize_parts` refuses a language-NEUTRAL key against
 * the `{lang}`-templated path. A dialog that created a bare `prompt` — which is
 * what the main-concern one does for its own family — would produce an asset
 * that reads back perfectly and can never be adopted. The catalog publishes the
 * pack's declared languages and its default, so the control offers those and
 * preselects that.
 *
 * ## OCCUPANCY IS SHOWN, AND THE SERVER STILL DECIDES
 *
 * One live asset may own a target (`uq_library_assets_active_placement`), so an
 * option another active asset already holds is disabled with the holder named.
 * That is derived from the catalog read this dialog already performs, never from
 * a rule restated here, and the 409 remains the authority — which is why a
 * refusal still renders against the control that carried it.
 *
 * A COPY PRESELECTS NO TARGET, on `NewMainConcernDialog`'s ruling (#366): the
 * source's target is held by the source, so offering it would offer the one
 * value guaranteed to be refused. The copy carries the source's CONTENT — every
 * part, examples included — which is what makes "copy the depression prompt"
 * mean the prompt and its example set together.
 */

const NAME_MAX_LENGTH = 80;

/** What a refusal computed in the BROWSER says, so nothing is ever swallowed. */
const REFUSED_CONTENT =
  "This Summary prompt's documents could not be prepared, so nothing was created.";

/**
 * The refusal for a complete target this runtime does not offer.
 *
 * It names the consequence rather than the rule, because the consequence is
 * what makes it worth refusing: placement is immutable, so a prompt created at
 * an unoffered target can only be corrected by archiving it and starting again.
 */
const UNOFFERED_TARGET =
  "This runtime does not currently offer that Summary target, so a prompt created there could not be adopted — and a prompt's target cannot be changed afterwards. Choose one of the targets listed, or try again once the clinical pack has been read.";

/**
 * The two shapes, as this dialog's own state.
 *
 * A local union rather than a partially-filled `SummaryTarget`: the wire type is
 * a discriminated union with every member required, and a form that has to model
 * "a diagnostic target whose evaluation is not chosen yet" cannot be that type
 * without lying about it. `submittedTarget` below is the one place the two meet,
 * and it answers `null` for every incomplete state — so the Create button's
 * enablement and the request body are derived from one expression.
 */
type TargetDraft =
  | { kind: "section"; section: string }
  | { kind: "diagnostic"; group: string; evaluation: string };

function submittedTarget(draft: TargetDraft): SummaryTarget | null {
  if (draft.kind === "section") {
    return draft.section === "" ? null : { kind: "section", section: draft.section };
  }
  return draft.group === "" || draft.evaluation === ""
    ? null
    : { kind: "diagnostic", group: draft.group, evaluation: draft.evaluation };
}

/**
 * Whether the catalog CURRENTLY offers this target.
 *
 * ## WHY COMPLETE IS NOT ENOUGH
 *
 * `submittedTarget` answers whether the form has been filled in. That is a
 * different question from whether the value is one this runtime reads, and the
 * two come apart in three real ways:
 *
 *   * a COPY of a seeded prompt opens on the source's target, which is the
 *     asset's fact rather than the catalog's;
 *   * `diagnosticSource: unavailable` — no readable pack — publishes no groups
 *     at all, so a diagnostic draft that survived from a copy names a group
 *     nothing can confirm;
 *   * a catalog refresh after a pack bump can stop offering a group the draft
 *     already holds.
 *
 * In each case the SERVER would accept the create: `summary_targets` validates
 * a nested target's grammar and leaves registration to adoption, deliberately,
 * so that a project may register its own groups. The result is a prompt that
 * reads back perfectly, cannot be adopted, and whose placement is immutable —
 * archiving and recreating is the only correction. Refusing here is the
 * difference between that and a sentence a clinician can act on.
 *
 * `undefined` catalog answers FALSE: an unread vocabulary confirms nothing, and
 * the prerequisite states which read is missing.
 */
function offeredByCatalog(
  catalog: SummaryTargetCatalog | undefined,
  target: SummaryTarget | null,
): boolean {
  if (catalog === undefined || target === null) {
    return false;
  }
  return target.kind === "section"
    ? catalog.sections.some((option) => option.section === target.section)
    : evaluationsOfGroup(catalog, target.group).some(
        (option) => option.evaluation === target.evaluation,
      );
}

/** What this dialog is waiting on, or why it cannot proceed. */
type Prerequisite =
  | { state: "pending" }
  | ({ state: "failed" } & NamedReadFailure)
  | { state: "unauthorable" }
  | { state: "ready" };

function readState(
  query: {
    isPaused: boolean;
    isPending: boolean;
    isError: boolean;
    error: unknown;
    failureReason: unknown;
  },
  retry: () => void,
): Prerequisite | null {
  if (query.isPaused) {
    return { state: "failed", failure: query.failureReason, isPaused: true, retry };
  }
  if (query.isError) {
    return { state: "failed", failure: query.error, isPaused: false, retry };
  }
  if (query.isPending) {
    return { state: "pending" };
  }
  return null;
}

/**
 * The refusal fields this route addresses, mapped to the control that carried
 * the value.
 *
 * `summaryTarget.section` and the two nested ones are reported at the select
 * that holds them; a bare `summaryTarget` — which is what a shape error and a
 * placement collision both collapse to — is reported against the whole target
 * group, because that is the granularity the service chose and inventing a
 * finer one here would point a clinician at a control the server did not name.
 */
function targetFieldErrors(error: unknown): {
  name?: string;
  target?: string;
  section?: string;
  group?: string;
  evaluation?: string;
  general?: string;
} {
  if (!(error instanceof ApiError)) {
    return {};
  }
  const field = libraryErrorField(error);
  switch (field) {
    case "name":
      return { name: error.message };
    case "summaryTarget":
      return { target: error.message };
    case "summaryTarget.section":
      return { section: error.message };
    case "summaryTarget.group":
      return { group: error.message };
    case "summaryTarget.evaluation":
      return { evaluation: error.message };
    default:
      // A 409 with no pointer is a name collision on this route; every other
      // conflict the contract declares here names its own field.
      return error.status === 409 ? { name: error.message } : { general: error.message };
  }
}

/**
 * One option's label, as a SINGLE string.
 *
 * A SEEDED holder reads "replaces …" and stays selectable: base canon stores no
 * coordinate, so the uniqueness index does not cover it and authoring there IS
 * the supported replacement — the seeded row's own refusal instructs it. Only an
 * authored holder is a refusal, and only that one disables the option.
 *
 * Written as one expression rather than as a slug beside a conditional
 * fragment, because accessible-name computation trims each text node and
 * concatenates inline content with NO separator: `{slug}{" — already used by X"}`
 * computes as `risk_assessment— already used by X`, which is what a clinician's
 * screen reader announces and what a test matching the visible text fails on.
 */
function occupiedOptionLabel(
  slug: string,
  holder: SummaryTargetHolder | undefined,
): string {
  if (holder === undefined) {
    return slug;
  }
  return holder.seeded
    ? `${slug} — replaces ${holder.name}`
    : `${slug} — already used by ${holder.name}`;
}

/**
 * What the target controls open on.
 *
 * A COPY OF BASE CANON OPENS ON THE SOURCE'S TARGET, and a copy of an authored
 * prompt opens on nothing. The two are opposite for one reason: base canon
 * stores no coordinate, so authoring at the one it reports is accepted and is
 * how a project replaces its prompt — the seeded row's own refusal says to copy
 * it. An AUTHORED source holds its target through the uniqueness index, so
 * offering it would offer the one value guaranteed to be refused (#366's ruling
 * on the main-concern dialog).
 */
function initialDraft(copyFrom: LibraryAsset | undefined): TargetDraft {
  const target =
    copyFrom?.assetType === "summary_prompt" && copyFrom.capabilities.seeded
      ? copyFrom.summaryTarget
      : null;
  if (!target) {
    return { kind: "section", section: "" };
  }
  return target.kind === "section"
    ? { kind: "section", section: target.section }
    : {
        kind: "diagnostic",
        group: target.group,
        evaluation: target.evaluation,
      };
}

export function NewSummaryPromptDialog({
  onClose,
  copyFrom,
}: {
  onClose: () => void;
  /** Opens as "create an editable copy of this asset". */
  copyFrom?: LibraryAsset;
}) {
  const navigate = useNavigate();
  const create = useCreateLibraryAssetMutation();
  const catalog = useSummaryTargetCatalogQuery();
  const manifest = useLibraryPartManifestQuery();
  // Archived rows included so occupancy can EXCLUDE them: archiving frees a
  // target, and a picker treating an archived row as an owner would refuse the
  // one correction the service documents for a wrong immutable placement.
  const assets = useLibraryAssetsQuery(true);

  const nameErrorId = useId();
  const targetErrorId = useId();
  const sectionErrorId = useId();
  const groupErrorId = useId();
  const evaluationErrorId = useId();

  const [name, setName] = useState(copyFrom ? `${copyFrom.name} (copy)` : "");
  const [draft, setDraft] = useState<TargetDraft>(() =>
    initialDraft(copyFrom),
  );
  const [language, setLanguage] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);

  const source = useAssetVersionQuery(
    copyFrom?.id ?? "",
    copyFrom ? copyFrom.latestVersion : null,
  );

  const retryCatalog = useRetryPausedQuery(queryKeys.summaryTargetCatalog);
  const retryManifest = useRetryPausedQuery(queryKeys.libraryPartManifest);
  const retrySource = useRetryPausedQuery(
    queryKeys.assetVersion(copyFrom?.id ?? "", copyFrom?.latestVersion ?? ""),
  );

  const entry = manifestFor(manifest.data, "summary_prompt");
  const required = requiredRoles(entry);
  const occupied = occupiedSummaryTargets(assets.data?.items ?? []);

  // The language state is seeded from the catalog's own default during render
  // rather than in an effect, on `useNamedDraft`'s precedent: an effect would
  // render one frame of an empty select over an answer already in hand. `null`
  // means "not chosen yet", which is distinct from a pack that declares none.
  const languages = catalog.data?.languages ?? [];
  const chosenLanguage =
    language !== null && languages.includes(language)
      ? language
      : (catalog.data?.defaultLanguage ?? languages[0] ?? null);

  const fieldErrors = targetFieldErrors(create.error);
  const generalMessage =
    blocked ??
    fieldErrors.general ??
    (create.isError && Object.keys(fieldErrors).length === 0
      ? "The Editor Service could not be reached, so nothing was created."
      : null);

  const busy = create.isPending;

  // A COPY DOES NOT NEED THE MANIFEST OR A LANGUAGE — it carries the source's
  // own parts — but it DOES need the catalog, because it still has to choose a
  // target. So the target read is a prerequisite on both paths and the content
  // reads differ, which is why these are composed rather than picked.
  const catalogState = readState(catalog, retryCatalog);
  const contentState = copyFrom
    ? readState(source, retrySource)
    : (readState(manifest, retryManifest) ??
      (entry === null || required.length === 0
        ? ({ state: "unauthorable" } as const)
        : chosenLanguage === null
          ? ({ state: "unauthorable" } as const)
          : null));
  const prerequisite: Prerequisite =
    catalogState ?? contentState ?? { state: "ready" };

  const target = submittedTarget(draft);
  const targetOccupiedBy = target ? occupied.get(summaryTargetKey(target)) : undefined;
  // A COMPLETE target that this runtime does not offer. Held separately from
  // "not filled in yet" because only one of the two has anything to say: an
  // empty form needs no sentence, and a target the catalog dropped needs one.
  //
  // GATED ON `isSuccess`, not merely on the data being absent. A copy opens
  // with its target already set, so during the catalog read `offeredByCatalog`
  // is correctly false and this would have flashed "not offered" at a clinician
  // about a target that turns out to be perfectly fine. An unread vocabulary
  // says nothing; the prerequisite's own loading state is what speaks then.
  const unofferedTarget =
    target !== null && catalog.isSuccess && !offeredByCatalog(catalog.data, target);

  function submit() {
    setBlocked(null);
    if (target === null) {
      return;
    }
    if (unofferedTarget) {
      // NEVER A SILENT RETURN, on this file's own rule: the clinician pressed
      // Create and is owed the reason. The button is already refused for this,
      // so reaching here means the two disagreed — and the sentence is the
      // thing that tells anybody so.
      setBlocked(UNOFFERED_TARGET);
      return;
    }

    let parts: DraftPart[];
    if (copyFrom) {
      const stored = source.isSuccess ? source.data.content : null;
      if (stored === null || !isPartsContent(stored)) {
        setBlocked(
          `The Editor Service returned ${copyFrom.name} in a form this editor does not recognise, so nothing was created.`,
        );
        return;
      }
      // EVERY part, in order: the prompt at every language it carries AND its
      // examples. "Preserve all example data" is not a special case here — it
      // is what copying the stored content means, and singling the prompt out
      // is what would lose them.
      parts = toDraft(stored);
    } else {
      if (chosenLanguage === null) {
        setBlocked(REFUSED_CONTENT);
        return;
      }
      parts = required.map((definition, index) => ({
        id: `new-${index}`,
        // Language-PREFIXED, which is the whole reason the catalog carries
        // languages: a neutral key is refused against the {lang}-templated path
        // when the project tries to adopt it.
        key: `${chosenLanguage}/${definition.role}`,
        kind: definition.kind as DraftPart["kind"],
        text: definition.kind === "structured" ? "{}" : UNWRITTEN_PROMPT_TEXT,
      }));
    }

    const content = toPartsContent(parts);
    if (!content.ok) {
      setBlocked(content.problems[0]?.message ?? REFUSED_CONTENT);
      return;
    }
    create.mutate(
      {
        assetType: "summary_prompt",
        name: name.trim(),
        content: content.content,
        note: copyFrom
          ? `Copied from "${copyFrom.name}" at ${copyFrom.latestVersion}.`
          : null,
        summaryTarget: target,
      },
      {
        onSuccess(created) {
          onClose();
          void navigate(`/library/summary/${created.id}`);
        },
      },
    );
  }

  const diagnosticUnavailable =
    catalog.isSuccess && catalog.data.diagnosticSource === "unavailable";
  const evaluations =
    draft.kind === "diagnostic"
      ? evaluationsOfGroup(catalog.data, draft.group)
      : [];

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={copyFrom ? "Create editable copy" : "New Summary prompt"}
        description={
          copyFrom
            ? "Creates your own Summary prompt starting from this one, with its prompt text and any examples. The original stays exactly as it is — choose the target the copy writes."
            : "One prompt, at one Summary target. The prompt text is written afterwards, from the prompt's own page."
        }
        busy={busy}
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              disabled={
                busy ||
                name.trim() === "" ||
                target === null ||
                unofferedTarget ||
                (targetOccupiedBy !== undefined && !targetOccupiedBy.seeded) ||
                prerequisite.state !== "ready"
              }
              onClick={submit}
            >
              {copyFrom ? "Create copy" : "Create Summary prompt"}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field
            label="Name"
            description="What this prompt is called in the library, and it has to be unique among the active Summary prompts. It is not the target: which prompt this is gets chosen below."
          >
            {({ inputId, describedBy }) => (
              <>
                <Input
                  id={inputId}
                  value={name}
                  maxLength={NAME_MAX_LENGTH}
                  placeholder="e.g. MOD depression findings"
                  aria-invalid={fieldErrors.name !== undefined || undefined}
                  aria-describedby={joinDescriptionIds(
                    describedBy,
                    fieldErrors.name ? nameErrorId : undefined,
                  )}
                  onChange={(event) => setName(event.target.value)}
                />
                {fieldErrors.name ? (
                  <FieldError id={nameErrorId} message={fieldErrors.name} />
                ) : null}
              </>
            )}
          </Field>

          <Field
            label="What this prompt writes"
            description="A section is one named part of the report. A diagnostic finding is one evaluation's paragraph inside the report's diagnostic-findings section."
          >
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                value={draft.kind}
                onChange={(event) =>
                  setDraft(
                    event.target.value === "section"
                      ? { kind: "section", section: "" }
                      : { kind: "diagnostic", group: "", evaluation: "" },
                  )
                }
              >
                <option value="section">Report section</option>
                <option value="diagnostic">Diagnostic finding</option>
              </Select>
            )}
          </Field>

          {draft.kind === "section" ? (
            <Field
              label="Section"
              description="The per-key sections this runtime reads. The diagnostic-findings section is not one of them — it holds the per-evaluation prompts instead."
            >
              {({ inputId, describedBy }) => (
                <>
                  <Select
                    id={inputId}
                    value={draft.section}
                    // BOTH ERRORS REACH THIS CONTROL. The server addresses a
                    // shape or grammar refusal at `summaryTarget.section`, but
                    // a placement collision and every malformed-target case
                    // collapse to a bare `summaryTarget` — which is the message
                    // a clinician most needs attached to something, because it
                    // is what a race against another writer produces.
                    aria-invalid={
                      fieldErrors.section !== undefined ||
                      fieldErrors.target !== undefined ||
                      undefined
                    }
                    aria-describedby={joinDescriptionIds(
                      describedBy,
                      fieldErrors.section ? sectionErrorId : undefined,
                      fieldErrors.target ? targetErrorId : undefined,
                    )}
                    onChange={(event) =>
                      setDraft({ kind: "section", section: event.target.value })
                    }
                  >
                    <option value="">Choose a section…</option>
                    {/* A COPY CAN OPEN ON A TARGET THE CATALOG NO LONGER LISTS,
                        and a select whose value matches no option renders
                        blank — which would show a cleared control beside a
                        refusal about a target the clinician cannot see. The
                        value is rendered as its own disabled entry instead. */}
                    {draft.section !== "" &&
                    catalog.isSuccess &&
                    !catalog.data.sections.some(
                      (option) => option.section === draft.section,
                    ) ? (
                      <option value={draft.section} disabled>
                        {`${draft.section} — not offered by this runtime`}
                      </option>
                    ) : null}
                    {(catalog.data?.sections ?? []).map((option) => {
                      const holder = occupied.get(sectionTargetKey(option.section));
                      return (
                        <option
                          key={option.section}
                          value={option.section}
                          disabled={holder !== undefined && !holder.seeded}
                        >
                          {occupiedOptionLabel(option.section, holder)}
                        </option>
                      );
                    })}
                  </Select>
                  {fieldErrors.section ? (
                    <FieldError id={sectionErrorId} message={fieldErrors.section} />
                  ) : null}
                </>
              )}
            </Field>
          ) : (
            <>
              <Field
                label="Condition"
                description="The dynamic track the evaluation belongs to, as the mounted clinical pack registers it."
              >
                {({ inputId, describedBy }) => (
                  <>
                    <Select
                      id={inputId}
                      value={draft.group}
                      disabled={diagnosticUnavailable}
                      // The bare `summaryTarget` refusal reaches both halves of
                      // a diagnostic target: the server names the pair, not one
                      // segment, so pointing at only one would send a clinician
                      // to the control that may not be the one to change.
                      aria-invalid={
                        fieldErrors.group !== undefined ||
                        fieldErrors.target !== undefined ||
                        undefined
                      }
                      aria-describedby={joinDescriptionIds(
                        describedBy,
                        fieldErrors.group ? groupErrorId : undefined,
                        fieldErrors.target ? targetErrorId : undefined,
                      )}
                      onChange={(event) =>
                        setDraft({
                          kind: "diagnostic",
                          group: event.target.value,
                          evaluation: "",
                        })
                      }
                    >
                      <option value="">Choose a condition…</option>
                      {draft.group !== "" &&
                      catalog.isSuccess &&
                      !catalog.data.diagnosticGroups.some(
                        (option) => option.group === draft.group,
                      ) ? (
                        <option value={draft.group} disabled>
                          {`${draft.group} — not offered by this runtime`}
                        </option>
                      ) : null}
                      {(catalog.data?.diagnosticGroups ?? []).map((option) => (
                        <option key={option.group} value={option.group}>
                          {option.group}
                        </option>
                      ))}
                    </Select>
                    {fieldErrors.group ? (
                      <FieldError id={groupErrorId} message={fieldErrors.group} />
                    ) : null}
                  </>
                )}
              </Field>

              <Field
                label="Evaluation"
                description="Which evaluation's findings this prompt writes. A condition with one evaluation names itself."
              >
                {({ inputId, describedBy }) => (
                  <>
                    <Select
                      id={inputId}
                      value={draft.evaluation}
                      disabled={draft.group === ""}
                      aria-invalid={
                        fieldErrors.evaluation !== undefined ||
                        fieldErrors.target !== undefined ||
                        undefined
                      }
                      aria-describedby={joinDescriptionIds(
                        describedBy,
                        fieldErrors.evaluation ? evaluationErrorId : undefined,
                        fieldErrors.target ? targetErrorId : undefined,
                      )}
                      onChange={(event) =>
                        setDraft({
                          kind: "diagnostic",
                          group: draft.group,
                          evaluation: event.target.value,
                        })
                      }
                    >
                      <option value="">
                        {draft.group === ""
                          ? "Choose a condition first"
                          : "Choose an evaluation…"}
                      </option>
                      {draft.evaluation !== "" &&
                      catalog.isSuccess &&
                      !evaluations.some(
                        (option) => option.evaluation === draft.evaluation,
                      ) ? (
                        <option value={draft.evaluation} disabled>
                          {`${draft.evaluation} — not offered by this runtime`}
                        </option>
                      ) : null}
                      {evaluations.map((option) => {
                        const holder = occupied.get(
                          diagnosticTargetKey(draft.group, option.evaluation),
                        );
                        return (
                          <option
                            key={option.evaluation}
                            value={option.evaluation}
                            disabled={holder !== undefined && !holder.seeded}
                          >
                            {occupiedOptionLabel(option.evaluation, holder)}
                          </option>
                        );
                      })}
                    </Select>
                    {fieldErrors.evaluation ? (
                      <FieldError
                        id={evaluationErrorId}
                        message={fieldErrors.evaluation}
                      />
                    ) : null}
                  </>
                )}
              </Field>
            </>
          )}

          {/* THE EXACT CLINICAL TARGET, as the server states it. Rendered from
              the catalog option's own `directory` — the server's expansion of
              its own template, with `{lang}` still in it — rather than assembled
              here. That is what the ticket means by an exact target identity: a
              clinician can read the file this prompt will write before they
              create it, and this application never spells that path. */}
          <TargetIdentity draft={draft} catalog={catalog.data} />

          {fieldErrors.target ? (
            <FieldError id={targetErrorId} message={fieldErrors.target} />
          ) : null}

          {unofferedTarget ? (
            <Notice tone="warning" role="alert">
              {UNOFFERED_TARGET}
            </Notice>
          ) : null}

          {targetOccupiedBy?.seeded ? (
            // NOT A REFUSAL. Base canon occupies this target and the service
            // accepts an authored prompt beside it; that is what makes the copy
            // a replacement rather than a duplicate, and a clinician is entitled
            // to know it before they create one.
            <Notice>
              Keeping this target makes this prompt replace{" "}
              {targetOccupiedBy.name} — the base clinical library's content at
              that runtime placement — for every project that adopts it.
            </Notice>
          ) : targetOccupiedBy ? (
            <Notice tone="warning">
              {targetOccupiedBy.name} already writes this target. One active
              authored prompt owns each target, so choose another — or archive
              that prompt first, which frees the target for a replacement.
            </Notice>
          ) : null}

          {diagnosticUnavailable && draft.kind === "diagnostic" ? (
            <UnavailableState
              title="The conditions this runtime registers could not be read"
              description="The Editor Service reports no mounted clinical pack, so which conditions and evaluations exist is unknown. Report sections are unaffected and can still be authored."
            />
          ) : null}

          {!copyFrom && languages.length > 1 ? (
            <Field
              label="Language"
              description="Which language tree this prompt's file is written into. The pack's other languages keep their existing content."
            >
              {({ inputId, describedBy }) => (
                <Select
                  id={inputId}
                  aria-describedby={describedBy}
                  value={chosenLanguage ?? ""}
                  onChange={(event) => setLanguage(event.target.value)}
                >
                  {languages.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}

          {prerequisite.state === "pending" ? (
            <LoadingState
              label={
                copyFrom
                  ? `Loading content from ${copyFrom.name}`
                  : "Loading the Summary targets this runtime reads"
              }
              rows={2}
            />
          ) : null}

          {prerequisite.state === "failed" ? (
            <NamedReadFailureState
              failure={prerequisite.failure}
              isPaused={prerequisite.isPaused}
              retry={prerequisite.retry}
              subject="Summary targets"
              title="This prompt cannot be created yet"
              description="The Editor Service did not return which Summary targets exist, and creating a prompt without one would be refused."
              deniedDescription="You are not authorized to read which Summary targets exist."
              offlineDescription="Which Summary targets exist could not be read, and creating a prompt without one would be refused. It loads on its own when the connection returns."
            />
          ) : null}

          {prerequisite.state === "unauthorable" ? (
            // A SUCCESSFUL read whose answer is that this cannot proceed —
            // nothing to retry. Either the service names no documents for this
            // type, or the mounted pack declares no language, and a prompt part
            // with no language prefix is one the adoption serializer refuses to
            // place.
            <UnavailableState
              title="A Summary prompt cannot be created here yet"
              description={
                languages.length === 0
                  ? "The Editor Service reports no clinical pack language to author this prompt at. A Summary prompt's file lives in a per-language tree, and creating one without a language would produce a prompt no project could adopt."
                  : "The Editor Service does not report which documents a Summary prompt must carry, so this editor cannot create one that it would accept."
              }
            />
          ) : null}

          {generalMessage ? (
            <Notice tone="destructive" role="alert">
              {generalMessage}
            </Notice>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The pack file the chosen target writes, or nothing.
 *
 * Its own component so the two shapes resolve their directory from the SAME
 * catalog entry the select offered — never from a string this file joins. An
 * incomplete choice renders nothing rather than a partial path: half a path is
 * a claim about a file, and there is no file yet.
 */
function TargetIdentity({
  draft,
  catalog,
}: {
  draft: TargetDraft;
  catalog: SummaryTargetCatalog | undefined;
}) {
  const directory =
    draft.kind === "section"
      ? catalog?.sections.find((option) => option.section === draft.section)
          ?.directory
      : evaluationsOfGroup(catalog, draft.group).find(
          (option) => option.evaluation === draft.evaluation,
        )?.directory;

  if (!directory) {
    return null;
  }

  return (
    <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
      <span className="font-medium text-foreground">Writes</span>{" "}
      <code dir="ltr" className="font-mono">
        {directory}
      </code>
    </div>
  );
}
